import React, { ComponentProps } from "react";
import Modal from "components/DesignSystem/Modal/Modal";
import { LockSecure } from "components/icons/LockSecure";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import {
  ConnectionsModal,
  ConnectionsModalProps,
} from "components/Connections/ConnectionsModal";
import { useBankConnect } from "hooks/useBankConnect";
import ReactCountryFlag from "react-country-flag";
import { Button } from "components/DesignSystem/Button/Button";
import { ArrowRight } from "components/icons/ArrowRight";
import { OtherBankingInstitution } from "components/Connections/OtherBankingInstitution";
import { useModal } from "hooks/useModal";
import { useUpdateQuery } from "hooks/useQuery";
import { AddManualBank } from "components/Connections/AddManualBank";
import { Entity } from "types/Models/entity";
import {
  ACCOUNT_CONNECTION_TYPE,
  AccountConnectionType,
} from "constants/dataSource";

type ConnectDataSourcesRootProps = {
  isOpen: boolean;
  closeConnectionModal: () => void;
  onConnectSuccess: () => void;
  currentTabType?: AccountConnectionType;
};

type SingleEntityProps = {
  entity: Entity;
  onEntitySelect: (entity: Entity) => void;
};

const AnimatedConnectionModalContent = ({
  onConnect,
  onComplete,
  onManualBankAddSuccess,
  entityIdFromParent,
  defaultTab,
}: ConnectionsModalProps &
  Pick<
    ComponentProps<typeof OtherBankingInstitution>,
    "onManualBankAddSuccess" | "onComplete"
  >) => {
  return (
    <section className="t-animate-height-scale-up">
      <ConnectionsModal
        onConnect={onConnect}
        onComplete={onComplete}
        onManualBankAddSuccess={onManualBankAddSuccess}
        entityIdFromParent={entityIdFromParent}
        defaultTab={defaultTab}
      />
    </section>
  );
};

const SingleEntity = ({ entity, onEntitySelect }: SingleEntityProps) => {
  return (
    <section
      data-testid="select_entity"
      onClick={() => onEntitySelect(entity)}
      className="t-border t-border-solid t-border-neutral-0 t-p-3 t-py-4 t-flex t-justify-between t-items-center t-w-full t-min-h-19 t-text-text-30 t-rounded-md t-cursor-pointer sm:t-gap-3"
    >
      <div className="t-flex t-gap-3 t-items-center sm:t-min-w-0">
        <div className="t-self-start"></div>
        <ReactCountryFlag
          className="!t-w-5 !t-h-3 t-rounded-sm t-object-cover sm:t-shrink-0"
          countryCode={entity?.country_code}
          svg
          title={entity?.country}
        />
        <div className="sm:t-min-w-0">
          <p className="t-mb-1 t-text-[#110733] sm:t-mb-0 sm:t-text-body sm:t-break-words">
            {entity?.name}
          </p>
        </div>
      </div>
      <Button size="small" className="sm:t-shrink-0">
        Select <ArrowRight color="currentColor" />
      </Button>
    </section>
  );
};

const ConnectDataSourcesRoot = ({
  isOpen,
  closeConnectionModal,
  onConnectSuccess,
  currentTabType,
}: ConnectDataSourcesRootProps) => {
  const { entities } = useCurrentGroupContext();
  const { onConnect } = useBankConnect({});
  const [selectedEntity, setSelectedEntity] = React.useState<string | null>(
    entities?.length === 1 ? entities[0].uuid : null
  );
  const otherBankingInstitutionModal = useModal();
  const { update } = useUpdateQuery();

  const onEntitySelect = (entity: Entity) => {
    setSelectedEntity(entity?.uuid);
    update({ query: "entity", value: entity?.uuid });
  };

  const onClose = () => {
    setSelectedEntity(entities?.length === 1 ? entities[0].uuid : null);
    closeConnectionModal();
  };

  const handlePlaidSuccess = () => {
    onConnectSuccess?.();
    otherBankingInstitutionModal.close();
    localStorage.removeItem("plaid_entity_id");
  };

  const manualSuccessSet = ({
    entityId,
  }: Parameters<
    NonNullable<ComponentProps<typeof AddManualBank>["onSuccess"]>
  >[0]) => {
    update({
      query: "entity",
      value: entityId,
    });
  };

  return (
    <section>
      <Modal.Root open={isOpen} onOpenChange={onClose}>
        <Modal.Content size="large" useCustomOverlay>
          <Modal.Header className="sm:t-pl-4">
            <Modal.Title>Connect Data Sources</Modal.Title>
            <Modal.Close />
          </Modal.Header>
          <Modal.Body className="sm:t-px-4">
            <section>
              {selectedEntity ? (
                <AnimatedConnectionModalContent
                  onConnect={onConnect}
                  onComplete={handlePlaidSuccess}
                  onManualBankAddSuccess={manualSuccessSet}
                  entityIdFromParent={selectedEntity}
                  defaultTab={
                    currentTabType !== ACCOUNT_CONNECTION_TYPE.ALL
                      ? currentTabType
                      : undefined
                  }
                />
              ) : (
                <section>
                  <p className="t-text-text-30 t-text-body-md sm:t-text-body">
                    Select the entity you want to connect data sources for:
                  </p>
                  <section className="t-flex t-flex-col t-gap-4">
                    {entities?.map((entity) => (
                      <SingleEntity
                        key={entity.uuid}
                        entity={entity}
                        onEntitySelect={onEntitySelect}
                      />
                    ))}
                  </section>
                </section>
              )}
            </section>
          </Modal.Body>
          <Modal.Footer className="sm:t-px-4">
            <div className="t-flex t-items-center t-gap-3">
              <div>
                <LockSecure />
              </div>
              <p className="t-m-0 t-text-text-30 t-text-body-sm">
                Inkle connects securely to your banks using industry-standard
                protocols, with read-only access to ensure your data stays safe
                and protected.
              </p>
            </div>
          </Modal.Footer>
        </Modal.Content>
      </Modal.Root>
    </section>
  );
};

export default ConnectDataSourcesRoot;
